import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  Card,
  DatePicker,
  Popconfirm,
  Space,
  Tag,
  Tooltip,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';
import {
  BACKEND_YEAR_FORMAT,
  DEFAULT_DATE_FORMAT,
  HOLIDAYS_TABLE_FIELDS,
  MODULES_KEY,
  PERMISSION_OBJECT,
} from '../../common/constants';
import CommonTable from '../../components/CommonTable';
import SearchComponent from '../../components/SearchComponent';
import VerifyPermissions from '../../components/VerifyPermissions';
import {
  antdSortOrder,
  mapAntTableChangeToParams,
  useTableQueryParams,
} from '../../hooks/useTableQueryParams';
import HolidayModal from './components/HolidayModal';
import { DELETE_HOLIDAY } from './graphql/Mutations';
import { HOLIDAYS } from './graphql/Queries';

const buildHolidaysFilter = (params) => ({
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
  year: params?.year,
  search: params?.search,
  orderBy: params?.sortField,
  order: params?.sortOrder,
});

function Holiday() {
  const { params, setParams } = useTableQueryParams({
    fields: HOLIDAYS_TABLE_FIELDS,
  });
  const [holidayId, setHolidayId] = useState();
  const [showModal, setShowModal] = useState(false);
  const [holidayRecord, setHolidayRecord] = useState();
  const isCreatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.HOLIDAYS,
    allowedPermissions: [PERMISSION_OBJECT?.create],
  });
  const isUpdatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.HOLIDAYS,
    allowedPermissions: [PERMISSION_OBJECT?.update],
  });
  const isDeletePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.HOLIDAYS,
    allowedPermissions: [PERMISSION_OBJECT?.delete],
  });
  const [executeQuery, { loading, data }] = useLazyQuery(HOLIDAYS, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const dataSource = data?.holidaysAdmin?.holidays || [];

  const [deleteHoliday] = useMutation(DELETE_HOLIDAY, {
    fetchPolicy: 'network-only',
    onCompleted: () => {
      executeQuery({
        variables: { filter: buildHolidaysFilter({ ...params, page: 1 }) },
      });
      setParams({ page: 1 });
    },
    onError: (error) => message?.error(`Error! ${error?.message}`),
  });

  useEffect(() => {
    executeQuery({ variables: { filter: buildHolidaysFilter(params) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleSearch = debounce((value) => {
    setParams({ search: value?.toString()?.trim(), page: 1 });
  }, 700);

  const handleYearChange = (value) => {
    const updatedValue = parseInt(
      dayjs(value)?.format(BACKEND_YEAR_FORMAT),
      10,
    );
    setParams({ year: updatedValue, page: 1 });
  };

  const confirm = () => {
    deleteHoliday({
      variables: {
        data: {
          id: holidayId,
        },
      },
    });
  };
  const handleDeleteClick = (record) => {
    setHolidayId(record?.id);
  };

  const handleEditClick = (record) => {
    setHolidayRecord(record);
    setShowModal(true);
  };

  const handleAddHoliday = () => {
    setHolidayRecord(null);
    setShowModal(true);
  };

  const onChange = (pagination, _filters, sorter) => {
    setParams(mapAntTableChangeToParams(pagination, sorter, params?.pageSize));
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: antdSortOrder(params, 'name'),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: true,
      sortOrder: antdSortOrder(params, 'startDate'),
      align: 'center',
      render: (_, record) =>
        dayjs(record?.startDate)?.format(DEFAULT_DATE_FORMAT),
    },
    {
      title: 'End Date',
      key: 'endDate',
      sorter: true,
      sortOrder: antdSortOrder(params, 'endDate'),
      align: 'center',
      render: (_, record) =>
        record?.startDate === record?.endDate
          ? '-'
          : dayjs(record?.endDate)?.format(DEFAULT_DATE_FORMAT),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      sorter: true,
      sortOrder: antdSortOrder(params, 'description'),
      align: 'center',
      render: (_, record) => record?.description || '-',
    },
    {
      title: 'Recurring',
      key: 'recurring',
      align: 'center',
      className: 'link-custom-width',
      render: (_, record) => (
        <Tag
          className="holiday-table-tags"
          color={record?.recurring ? 'success' : 'error'}
        >
          {record?.recurring ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    ...(isUpdatePermission || isDeletePermission
      ? [
          {
            title: 'Action',
            key: 'action',
            align: 'center',
            width: 120,
            render: (_, record) => (
              <Space size="middle">
                {isUpdatePermission && (
                  <Tooltip placement="bottom" title={<span>Edit Holiday</span>}>
                    <Button
                      type="text"
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleEditClick(record);
                      }}
                    >
                      <EditOutlined />
                    </Button>
                  </Tooltip>
                )}
                {isDeletePermission && (
                  <Popconfirm
                    title="Delete Holiday"
                    description="Are you sure to delete this Holiday?"
                    onConfirm={confirm}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Tooltip
                      placement="bottom"
                      title={<span>Delete Holiday</span>}
                    >
                      <Button
                        danger
                        type="text"
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleDeleteClick(record);
                        }}
                      >
                        <DeleteOutlined />
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="table-card-page">
      <Card
        className="ant-body-scroll"
        title="Holidays"
        extra={
          <>
            <div className="holidays-sidebar">
              <div className="holiday-filter-right">
                {isCreatePermission && (
                  <Button key="1" type="primary" onClick={handleAddHoliday}>
                    Add Holiday
                  </Button>
                )}
                <SearchComponent
                  key={params?.search || ''}
                  defaultValue={params?.search}
                  getData={handleSearch}
                  name="holiday"
                />
              </div>
            </div>
          </>
        }
      >
        <div className="holiday-filter-left p-8">
          <strong className="mr-8">
            Select the year for which you want to add or check holidays:
          </strong>
          <DatePicker
            className="holiday-year-picker"
            value={dayjs()?.year(params?.year)}
            format="YYYY"
            onChange={handleYearChange}
            picker="year"
            allowClear={false}
          />
        </div>
        <CommonTable
          className="holiday-common-table"
          size="small"
          loadingData={loading}
          columns={columns}
          dataSource={dataSource}
          rowKey={(obj) => obj?.id}
          bordered
          onRow={(record) => ({
            onClick: () =>
              isUpdatePermission ? handleEditClick(record) : undefined,
          })}
          rowClassName={() => (isUpdatePermission ? 'pointer' : '')}
          onChange={onChange}
          paginationConfig={{
            current: params?.page,
            total: data?.holidaysAdmin?.count,
            pageSize: params?.pageSize,
          }}
        />

        {showModal && (
          <HolidayModal
            holidayRecord={holidayRecord}
            executeQuery={executeQuery}
            setHolidayRecord={setHolidayRecord}
            show={showModal}
            close={() => setShowModal(false)}
            initialFilter={{ year: params?.year }}
            setPaginationFilter={({ currentPage }) =>
              setParams({ page: currentPage })
            }
            paginationFilter={{
              currentPage: params?.page,
              pageSize: params?.pageSize,
            }}
          />
        )}
      </Card>
    </div>
  );
}

export default Holiday;
